import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { Vessel } from '../../dto/vessel';
import { FileService } from '../../../../common/file/service/file.service';
import { VesselRedundancyDiagramService } from '../../service/vessel-redundancy-diagram.service';
import { VesselRedundancyDiagram } from '../../dto/vessel-redundancy-diagram';

@Component({
  selector: 'dpi-vessel-redundancy-diagram-list',
  imports: [MatCardModule, MatIconModule, MatButtonModule, MatToolbarModule, MatButtonToggleModule, MatTooltipModule, MatPaginatorModule, NgIf],
  templateUrl: './vessel-redundancy-diagram-list.component.html',
  styleUrl: './vessel-redundancy-diagram-list.component.scss'
})
export class VesselRedundancyDiagramListComponent implements OnInit, OnDestroy {
  vessel: Vessel;
  listDiagram: VesselRedundancyDiagram[] = [];
  imgFromApi: string = '';
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isReadonly: boolean;

  pageLength = 0;
  pageSize = 1;
  pageIndex = 0;
  pageSizeOptions = [1];
  hidePageSize = true;
  showPageSizeOptions = false;
  showFirstLastButtons = true;
  disabled = false;
  pageEvent: PageEvent | undefined;
  handlePageEvent(e: PageEvent) {
    this.pageEvent = e;
    this.pageLength = e.length;
    this.pageSize = e.pageSize;
    this.pageIndex = e.pageIndex;
    this.reloadImages(this.vessel.id);
  }
  
  // 配置项
  allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf']; // 允许的文件类型
  maxSizeMB = 100; // 最大 100MB

  // 状态
  selectedFile: File | null = null;
  isDragging = false;
  uploadError = '';

  constructor(private notifyService: StateNotifyService, private diagramService: VesselRedundancyDiagramService, private fileService: FileService) {
    this.vessel = this.notifyService.getSelectedVessel();
    this.isReadonly = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.vesselSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.vessel = model;
        this.loadListDiagram(model.id, true);
      })

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isReadonly = res;
        this.resetStatus()
      });
  }

  loadListDiagram(vesselId: number, displayFromFirstOne: boolean) {
    this.diagramService.getAllRedundancyDiagramsOfVessel(vesselId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          this.listDiagram = res;
          this.listSize.emit(this.listDiagram.length)
          if (displayFromFirstOne) {
            this.pageIndex = 0;
          } else {
            this.pageIndex = this.listDiagram.length - 1;
          }
          this.reloadImages(vesselId);
        },
        error: (error: any) => {
          console.error('Error: ', error);
          this.listDiagram = [];
        }
      });
  }

  reloadImages(vesselId: number) {
    this.pageLength = this.listDiagram.length;
    if (this.pageLength > 0) {
      const diagramRecord = this.listDiagram[this.pageIndex];
      this.diagramService.getVesselRedundancyDiagram(diagramRecord.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (img: any) => {
            this.imgFromApi = this.fileService.arrayBufferToBase64(img);
          },
          error: (error: any) => {
            console.error('Error: ', error);
            this.imgFromApi = '';
          }
        });
    } else {
      this.imgFromApi = '';
    }

  }
  
  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (file) {
      this.diagramService.fileUpload(this.vessel.id, file).subscribe({
        next: res => {
          this.loadListDiagram(this.vessel.id, false);
        },
        error: err => {
          console.log(err);
        }
      });
    }
  }

  deleteDiagram() {
    if (this.pageLength > 0) {
      const diagramRecord = this.listDiagram[this.pageIndex];
      this.diagramService.deleteVesselRedundancyDiagram(diagramRecord.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (res: any) => {
            this.loadListDiagram(this.vessel.id, true);
          },
          error: (error: any) => {
            console.error('Error: ', error);
          }
        });
    }
  }

  // ------------------------------
  // 拖放事件
  // ------------------------------
  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave() {
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.validateAndSetFile(files[0]);
    }
  }

  // ------------------------------
  // 文件验证与上传
  // ------------------------------
  validateAndSetFile(file: File) {
    this.resetStatus();

    // 检查类型
    if (!this.allowedTypes.includes(file.type)) {
      this.uploadError = '不支持此文件类型';
      return;
    }

    // 检查大小
    const maxSize = this.maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      this.uploadError = `文件不能超过 ${this.maxSizeMB}MB`;
      return;
    }

    this.selectedFile = file;

    if (file) {
      this.diagramService.fileUpload(this.vessel.id, file).subscribe({
        next: res => {
          this.loadListDiagram(this.vessel.id, false);
        },
        error: err => {
          console.log(err);
        }
      });
    }

  }

  // 重置
  resetStatus() {
    this.selectedFile = null;
    this.uploadError = '';
  }



}
